import type { CSSProperties, ReactNode, MouseEvent, HTMLAttributes } from 'react'
import styled from 'styled-components'

import { Color } from '../../theme/color'
import type { ColorKeys } from '../../theme/color'

/**
 * 400: regular
 * 500: medium
 * 600: semi-bold
 * 700: bold
 */
type Weight = 400 | 500 | 600 | 700

interface TextProps {
  size: number
  weight?: Weight
  lineHeight?: number
  color?: ColorKeys
  block?: boolean
  $wordBreak?: CSSProperties['wordBreak']
  $textAlign?: CSSProperties['textAlign']
  $textOverflow?: CSSProperties['textOverflow']
  style?: CSSProperties
  className?: HTMLAttributes<HTMLSpanElement>['className']
  children?: ReactNode
  onClick?: (e: MouseEvent<HTMLSpanElement>) => void
}

export function Text({
  size = 14,
  weight = 400,
  $wordBreak = 'break-all',
  lineHeight,
  color,
  block,
  $textAlign,
  $textOverflow,
  style,
  className,
  children,
  onClick,
}: TextProps) {
  return (
    <StyledText
      size={size}
      weight={weight}
      lineHeight={lineHeight}
      color={color}
      block={block}
      $wordBreak={$wordBreak}
      $textAlign={$textAlign}
      $textOverflow={$textOverflow}
      style={style}
      className={className}
      onClick={onClick}
    >
      {children}
    </StyledText>
  )
}

export const StyledText = styled.span<TextProps>`
  overflow: hidden;
  white-space: pre-wrap;
  ${({ block }) => `display: ${block ? 'block' : 'inline'};`}
  ${({ size }) => `font-size: ${size}px;`}
  ${({ weight }) => `font-weight: ${weight};`}
  ${({ lineHeight }) => `line-height: ${lineHeight ? `${lineHeight}px` : 'auto'};`}
  ${({ color }) => `color: ${color ? Color[color] : Color.text_primary};`}
  ${({ $wordBreak }) => `word-break: ${$wordBreak};`}
  ${({ $textAlign }) => `text-align: ${$textAlign};`}
  ${({ $textOverflow }) =>
    `text-overflow: ${$textOverflow}; ${$textOverflow === 'ellipsis' ? 'white-space: nowrap;' : ''}`}
`

export type { TextProps }
